import { Box } from "@repo/components/src/ui/box";
import { HStack } from "@repo/components/src/ui/hstack";
import { Image } from "@repo/components/src/ui/image";
import { Pressable } from "@repo/components/src/ui/pressable";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { Link } from "expo-router";
import React from "react";
import { FORMAT_CARDS } from "./formatCardsData";

type Props = {
  /** compact = 1a's format grid (with meta row). large = 1b's format grid (no meta row). */
  variant: "compact" | "large";
  brandId: string;
};

const BADGE_LABEL = { popular: "MÁS USADO", recommended: "RECOMENDADO" };

/**
 * The three format shortcuts (UGC / Orgánico / Meliclips) both "new campaign"
 * screens end with. Clicking a card is a navigation, not a selection — it
 * drops straight into the AI chat pre-set to that type, same contract as
 * `MontuAiLauncher`'s `type` param.
 */
const FormatCards = React.memo(({ variant, brandId }: Props) => {
  const compact = variant === "compact";

  return (
    <HStack space={compact ? "md" : "lg"} className="w-full flex-wrap">
      {FORMAT_CARDS.map((card) => {
        // Badges are 1a-only: on the 1b (large) grid Montu AI does the
        // choosing, so all three cards render identically.
        const badge = compact ? card.badgeCompact : undefined;
        const badgeLabel = badge ? BADGE_LABEL[badge] : null;

        return (
          <Link
            key={card.slug}
            asChild
            href={{
              pathname: "/app/(brands)/[brandId]/(main)/campaign/ai",
              params: { brandId, new: "1", type: card.slug },
            }}
          >
            <Pressable
              className={
                compact
                  ? "min-w-[220px] flex-1 basis-0 rounded-[20px] border border-outline-100 bg-background-0 p-[22px] transition-all duration-150 hover:border-brand-300 hover:shadow-card-hover"
                  : "min-w-[260px] flex-1 basis-0 rounded-[22px] border-[1.5px] border-outline-100 bg-background-0 p-6 transition-all duration-150 hover:border-brand-300"
              }
            >
              <VStack space={compact ? "sm" : "md"}>
                <HStack className="items-start justify-between">
                  <Box
                    className={`items-center justify-center bg-icon-tile ${
                      compact
                        ? "h-[62px] w-[62px] rounded-[18px]"
                        : "h-[74px] w-[74px] rounded-[22px]"
                    }`}
                  >
                    <Box
                      className={
                        compact
                          ? card.illustrationClass
                          : card.illustrationClassLarge
                      }
                    >
                      <Image
                        source={card.illustration}
                        alt=""
                        size="full"
                        contentFit="contain"
                      />
                    </Box>
                  </Box>
                  {badgeLabel ? (
                    <Box className="rounded-full bg-brand-50 px-2.5 py-1.5">
                      <Text
                        size="2xs"
                        bold
                        className="uppercase tracking-wide text-brand-600"
                      >
                        {badgeLabel}
                      </Text>
                    </Box>
                  ) : null}
                </HStack>
                <VStack space="xs">
                  <Text
                    bold
                    className={`text-typography-900 ${compact ? "text-lg" : "text-xl"}`}
                  >
                    {card.title}
                  </Text>
                  <Text
                    size={compact ? "sm" : "md"}
                    className="leading-relaxed text-typography-500"
                  >
                    {compact ? card.compactDescription : card.largeDescription}
                  </Text>
                </VStack>
              </VStack>
            </Pressable>
          </Link>
        );
      })}
    </HStack>
  );
});

FormatCards.displayName = "FormatCards";

export default FormatCards;
